import type {
  FinanceOverviewResponse,
  FinanceOverviewTrendCurrency,
} from "../../finance/overview-types";

interface FinanceOverviewTrendChartProps {
  report: FinanceOverviewResponse;
  currency: string;
}

const series = [
  { key: "net_sales", label: "Net Sales", colour: "#059669" },
  { key: "cogs", label: "COGS", colour: "#f59e0b" },
  { key: "gross_profit", label: "Gross Profit", colour: "#2563eb" },
] as const;

function niceStep(range: number, targetIntervals = 4): number {
  const roughStep = range / targetIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const multiplier =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return multiplier * magnitude;
}

function yAxisScale(minimum: number, maximum: number) {
  const step = niceStep(maximum - minimum);
  const scaleMinimum = Math.floor(minimum / step) * step;
  const scaleMaximum = Math.ceil(maximum / step) * step;
  const ticks: number[] = [];

  for (
    let tick = scaleMinimum;
    tick <= scaleMaximum + step / 2;
    tick += step
  ) {
    ticks.push(Number(tick.toPrecision(12)));
  }

  return { minimum: scaleMinimum, maximum: scaleMaximum, step, ticks };
}

function formatAxisMoney(value: number, currency: string, step: number): string {
  const compact = Math.abs(value) >= 1000;
  const maximumFractionDigits = compact ? 1 : step < 1 ? 2 : step < 10 ? 1 : 0;

  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
}

function plottedValue(
  values: FinanceOverviewTrendCurrency | undefined,
  key: (typeof series)[number]["key"],
) {
  const value = values?.[key];
  if (value !== null && value !== undefined) {
    return value;
  }
  if (key === "gross_profit") {
    return (values?.known_net_sales ?? 0) - (values?.known_cogs ?? 0);
  }

  return key === "net_sales"
    ? values?.known_net_sales ?? 0
    : values?.known_cogs ?? 0;
}

export default function FinanceOverviewTrendChart({
  report,
  currency,
}: FinanceOverviewTrendChartProps) {
  const points = report.trend.map((point) => ({
    label: point.label,
    values: point.by_currency.find((item) => item.currency === currency),
  }));
  const knownValues = points.flatMap((point) =>
    series.map((item) => plottedValue(point.values, item.key)),
  );
  const dataMinimum = Math.min(0, ...knownValues);
  const dataMaximum = Math.max(1, ...knownValues);
  const yAxis = yAxisScale(dataMinimum, dataMaximum);
  const minimum = yAxis.minimum;
  const maximum = yAxis.maximum;
  const span = maximum - minimum;
  const width = 720;
  const height = 240;
  const topPadding = 30;
  const bottomPadding = 30;
  const leftPadding = 72;
  const rightPadding = 30;
  const usableWidth = width - leftPadding - rightPadding;
  const usableHeight = height - topPadding - bottomPadding;
  const yPosition = (value: number) =>
    topPadding + usableHeight - ((value - minimum) / span) * usableHeight;
  const polyline = (key: (typeof series)[number]["key"]) =>
    points
      .map((point, index) => {
        const plotted = plottedValue(point.values, key);
        const x =
          leftPadding +
          (points.length === 1
            ? usableWidth / 2
            : (index / (points.length - 1)) * usableWidth);
        const y = yPosition(plotted);
        return `${x},${y}`;
      })
      .join(" ");

  return (
    <div>
      <div className="flex flex-wrap gap-4 text-xs font-medium text-slate-600">
        {series.map((item) => (
          <span key={item.key} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: item.colour }}
            />
            {item.label}
          </span>
        ))}
      </div>
      <svg
        className="mt-4 h-auto w-full"
        viewBox={`0 0 ${width} ${height + 28}`}
        role="img"
        aria-label={`${currency} Net Sales, COGS, and Gross Profit trend`}
      >
        <text
          x={leftPadding - 10}
          y={14}
          textAnchor="end"
          className="fill-slate-500 text-xs font-medium"
        >
          {currency}
        </text>
        {yAxis.ticks.map((tick) => {
          const y = yPosition(tick);
          return (
            <g key={tick}>
              <line
                x1={leftPadding}
                x2={width - rightPadding}
                y1={y}
                y2={y}
                stroke="var(--color-border)"
              />
              <text
                x={leftPadding - 10}
                y={y}
                dy="0.32em"
                textAnchor="end"
                className="fill-slate-500 text-xs tabular-nums"
              >
                {formatAxisMoney(tick, currency, yAxis.step)}
              </text>
            </g>
          );
        })}
        <line
          x1={leftPadding}
          x2={leftPadding}
          y1={topPadding}
          y2={topPadding + usableHeight}
          stroke="var(--color-brand-muted)"
          strokeOpacity="0.45"
        />
        {series.map((item) => (
          <polyline
            key={item.key}
            points={polyline(item.key)}
            fill="none"
            stroke={item.colour}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {points.map((point, index) => {
          const x =
            leftPadding +
            (points.length === 1
              ? usableWidth / 2
              : (index / (points.length - 1)) * usableWidth);
          return (
            <text
              key={point.label}
              x={x}
              y={height + 10}
              textAnchor="middle"
              className="fill-slate-500 text-[10px]"
            >
              {point.label}
            </text>
          );
        })}
      </svg>
      {points.some((point) => point.values?.complete === false) && (
        <p className="mt-2 text-xs text-amber-700">
          Incomplete periods use known values where source records are missing.
        </p>
      )}
    </div>
  );
}
